/* eslint-disable complexity */
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Button, Divider } from 'src/components/atoms';
import { InformationBanner, Modal } from 'src/components/molecules';
import { DividerContainer } from 'src/components/molecules/InviteToCaseModal/Steps/AccessStep.styles';
import { Access } from 'src/components/molecules/InviteToCaseModal/components/Access/Access';
import {
  CASE_TABLE_PERMISSIONS,
  accessStepPermissionHelper,
} from 'src/components/molecules/InviteToCaseModal/helpers/accessStepPermissionHelper';
import { ModalContainer } from 'src/components/molecules/ModalContainer';
import {
  toggleShowEditCaseMemberModal,
  toggleShowRemoveCaseMemberModal,
  updateSelectedCaseTeamMemberPermissions,
} from 'src/components/pages/Case/Case.slice';
import { formatDateTime } from 'src/components/pages/Cases/helpers/formatDate';
import {
  useEditCaseAccessMutation,
  useGetCaseTeamMemberPermissionsQuery,
  useRemoveFromCaseMutation,
} from 'src/graphql';
import { AppDispatch } from 'src/store';
import {
  CasePermissionsType,
  CaseTeamMemberPermissionsType,
  FormState,
  Nullable,
} from 'src/types';
import { getInitials } from 'src/utils/getInitials';
import { EditCaseTeamMember } from './EditCaseTeamMember';
import {
  CloseIcon,
  FooterContainer,
  Name,
  TitleContainer,
} from './ReviewCaseTeamMemberModal.styles';
import {
  editCaseTeamMemberAccess,
  filterPermissions,
  removeCaseTeamMember,
  reviewCaseTeamMemberModalVM,
} from './ReviewCaseTeamMemberModal.vm';
import { HeaderRow } from './components';
import { DetailsRow } from './components/DetailsRow';
import { GlobalAccessDetails } from './components/GlobalAccessDetails';
import { Note } from './components/Note';

interface ReviewCaseTeamMemberModalProps {
  open: boolean;
  closeModal: () => void;
  userId: Nullable<number>;
  caseId: string;
  footer?: React.ReactNode;
  showNoteTop: boolean;
}

export const ReviewCaseTeamMemberModal = ({
  open,
  closeModal,
  caseId,
  userId,
  showNoteTop,
  footer,
}: ReviewCaseTeamMemberModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: caseTeamMemberPermissions } =
    useGetCaseTeamMemberPermissionsQuery({
      caseId,
      userId,
    });

  useEffect(() => {
    dispatch(
      updateSelectedCaseTeamMemberPermissions(
        (caseTeamMemberPermissions?.getCaseTeamMemberPermissions ??
          []) as CasePermissionsType[],
      ),
    );
    return () => {
      dispatch(updateSelectedCaseTeamMemberPermissions([]));
    };
  }, [dispatch, caseTeamMemberPermissions]);

  const {
    name,
    site,
    role,
    country,
    status,
    caseDisplayId,
    caseTeamNoteText,
    showEditCaseMemberModal,
    showRemoveCaseMemberModal,
    invitedAt,
    permissions,
    filteredPermissions,
    canEditPermissions,
    unableToChat,
    caseLeadInviter,
    isReportAssignee,
    isSuperAdmin,
  } = useSelector(reviewCaseTeamMemberModalVM(userId));
  const [permissionsState, setPermissionsState] =
    useState<CasePermissionsType[]>(filteredPermissions);
  const [editAccessMode, setEditAccessMode] = useState<boolean>(false);

  useEffect(() => {
    // update permissionsState for modal on open and close
    setPermissionsState(
      filterPermissions(
        (caseTeamMemberPermissions?.getCaseTeamMemberPermissions ??
          []) as CaseTeamMemberPermissionsType[],
      ) as CasePermissionsType[],
    );
  }, [caseTeamMemberPermissions?.getCaseTeamMemberPermissions]);

  useEffect(() => {
    setEditAccessMode(false);
  }, []);

  const {
    hasAllViewPermissions,
    hasAllEditPermissions,
    hasSomeEditPermissions,
    disabledViewReportPII,
    disabledViewCasePII,
  } = accessStepPermissionHelper(permissions, unableToChat);

  const queryClient = useQueryClient();

  const { mutateAsync: removeFromCase } = useRemoveFromCaseMutation();
  const { mutateAsync: editCaseAccess } = useEditCaseAccessMutation();

  const handleEditPermissions = (
    updatedPermissions: CasePermissionsType[],
    action: 'add' | 'remove',
  ) => {
    if (action === 'add') {
      setPermissionsState((prevState) => [
        ...new Set([...prevState, ...updatedPermissions]),
      ]);
    } else {
      if (isSuperAdmin && updatedPermissions.includes('SEND_REPORT_MESSAGES')) {
        // Super Admins should always keep the `VIEW_REPORT_MESSAGES` permission
        setPermissionsState((prevState) => [
          ...prevState.filter(
            (permission) => !updatedPermissions.includes(permission),
          ),
          'VIEW_REPORT_MESSAGES',
        ]);
      } else if (
        // Report Assignees should always keep the `VIEW_CASE_DETAILS` permission
        // and `VIEW_CASE_DETAILS_PII` permission if it's checked
        isReportAssignee &&
        updatedPermissions.includes('EDIT_CASE_DETAILS')
      ) {
        const piiPermission = permissionsState.includes(
          'VIEW_CASE_DETAILS_PII',
        );
        setPermissionsState((prevState) => [
          ...prevState.filter(
            (permission) => !updatedPermissions.includes(permission),
          ),
          ...(piiPermission
            ? (['VIEW_CASE_DETAILS_PII'] as CasePermissionsType[])
            : []),
          'VIEW_CASE_DETAILS',
        ]);
      } else {
        setPermissionsState((prevState) =>
          prevState.filter(
            (permission) => !updatedPermissions.includes(permission),
          ),
        );
      }
    }
  };

  const triggerCloseModal = () => {
    setEditAccessMode(false);
    closeModal();
  };

  return (
    <ModalContainer
      data-testid="review-case-team-member-modal"
      open={open}
      closeModal={triggerCloseModal}
      title={
        <>
          <CloseIcon
            size="24px"
            className="uil uil-multiply"
            data-testid="review-case-team-member-modal-close-icon"
            onClick={triggerCloseModal}
          />
          <TitleContainer>
            {!editAccessMode && (
              <Avatar text={getInitials(name)} variant="text" size="medium" />
            )}
            <Name>{editAccessMode ? 'Edit access' : name}</Name>
          </TitleContainer>
          {isReportAssignee && (
            <InformationBanner
              data-testid="report-assignee-warning"
              state={FormState.WARNING}
              title="Report Assignee's have access granted by their role definition"
              description="To remove access, you will need to reassign the Report Assignee."
            />
          )}
          <Divider variant="horizontal" />
        </>
      }
      footer={
        footer ?? (
          <>
            <Divider variant="horizontal" />
            <FooterContainer>
              {canEditPermissions && (
                <Button
                  variant="primary"
                  width="140px"
                  disabled={
                    editAccessMode &&
                    _.isEqual(
                      _.sortBy(filteredPermissions),
                      _.sortBy(permissionsState),
                    )
                  }
                  onClick={() => {
                    if (editAccessMode) {
                      if (
                        _.isEqual(
                          _.sortBy(CASE_TABLE_PERMISSIONS),
                          _.sortBy(permissionsState),
                        )
                      ) {
                        dispatch(
                          toggleShowRemoveCaseMemberModal({ open: true }),
                        );
                      } else {
                        dispatch(toggleShowEditCaseMemberModal({ open: true }));
                      }
                    } else {
                      setEditAccessMode(true);
                    }
                  }}
                >
                  {editAccessMode ? 'Save changes' : 'Edit access'}
                </Button>
              )}
              {editAccessMode && (
                <Button
                  variant="secondary"
                  width="140px"
                  onClick={() => {
                    setPermissionsState(filteredPermissions);
                    setEditAccessMode(false);
                  }}
                >
                  Cancel
                </Button>
              )}
              {canEditPermissions && !isReportAssignee && !editAccessMode && (
                <Button
                  variant="secondary"
                  width="140px"
                  onClick={() =>
                    dispatch(toggleShowRemoveCaseMemberModal({ open: true }))
                  }
                >
                  Remove
                </Button>
              )}
            </FooterContainer>
          </>
        )
      }
    >
      {editAccessMode ? (
        <EditCaseTeamMember
          permissions={permissionsState}
          userId={userId}
          showNoteTop={showNoteTop}
          handleEditPermissions={handleEditPermissions}
        />
      ) : (
        <>
          {showNoteTop && caseTeamNoteText && (
            <Note
              caseTeamNoteText={caseTeamNoteText}
              title={`Note ${
                caseLeadInviter ? `from ${caseLeadInviter}` : ''
              } sent on ${formatDateTime(invitedAt)}`}
            />
          )}
          <HeaderRow icon="user" title="Teammate profile" />
          <DetailsRow
            name={name}
            site={site}
            role={role}
            country={country}
            status={status}
            invitedAt={invitedAt}
          />
          {(hasAllEditPermissions || hasAllViewPermissions) && (
            <GlobalAccessDetails
              hasAllEditPermissions={hasAllEditPermissions}
              hasSomeEditPermissions={hasSomeEditPermissions}
              hasAllViewPermissions={hasAllViewPermissions}
              displayDivider={true}
              caseDisplayId={caseDisplayId}
            />
          )}
          <Access
            permissions={permissionsState}
            handleSetPermissions={handleEditPermissions}
            disabledViewCasePII={disabledViewCasePII || !editAccessMode}
            disabledViewReportPII={disabledViewReportPII || !editAccessMode}
            unableToChat={unableToChat}
            disablePermissions={!canEditPermissions || !editAccessMode}
            isSuperAdmin={isSuperAdmin}
            isReportAssignee={isReportAssignee}
          />
          {!showNoteTop && (
            <>
              <DividerContainer>
                <Divider variant={'horizontal'} />
              </DividerContainer>
              <Note
                caseTeamNoteText={caseTeamNoteText}
                title="Attached note"
                data-testid="review-case-team-member-modal-note"
              />
            </>
          )}
        </>
      )}
      <Modal
        open={showEditCaseMemberModal}
        setOpen={(open: boolean) =>
          dispatch(toggleShowEditCaseMemberModal({ open }))
        }
        size="medium"
        title="Confirm changed to case access"
        description={
          <p>
            Are you sure you want to edit the level of access for the invited
            team member? Please ensure that this adjustment aligns with your
            intended changes before proceeding.
          </p>
        }
        confirmButtonText="Confirm changes"
        cancelButtonText="Cancel"
        onCancel={() =>
          dispatch(toggleShowEditCaseMemberModal({ open: false }))
        }
        onConfirm={() => {
          if (userId) {
            dispatch(
              editCaseTeamMemberAccess({
                caseId,
                userId,
                entityPermissions: permissionsState,
                mutationEditCaseAccess: editCaseAccess,
                queryClient,
              }),
            );
            triggerCloseModal();
          }
        }}
      />
      <Modal
        open={showRemoveCaseMemberModal}
        setOpen={(open: boolean) =>
          dispatch(toggleShowRemoveCaseMemberModal({ open }))
        }
        size="medium"
        title="Are you sure you want to remove this team member?"
        description={
          <>
            <p>
              Before you proceed, please consider the implications of removing a
              team member from this case. This action cannot be undone and will
              revoke their access to the case&apos;s information and
              collaboration tools.
            </p>
            <p>
              Removing a team member may impact the progress and efficiency of
              your case. Make sure to communicate any changes within your team
              to ensure seamless workflow.
            </p>
          </>
        }
        confirmButtonText="Remove team member"
        cancelButtonText="Cancel"
        onCancel={() =>
          dispatch(toggleShowRemoveCaseMemberModal({ open: false }))
        }
        onConfirm={() => {
          if (userId) {
            dispatch(
              removeCaseTeamMember({
                caseId,
                userId,
                mutationRemoveCaseTeamMember: removeFromCase,
                queryClient,
              }),
            );
            triggerCloseModal();
          }
        }}
      />
    </ModalContainer>
  );
};
